import { Link, useLocation } from "react-router-dom";
import { CAPABILITIES, CAP_IDS } from "@/content/software";
import { PageIntro } from "@/components/site/PageIntro";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { Container, Eyebrow, Mono, SectionTitle, StatusMarker, TextLink } from "@/components/site/primitives";
import { CapabilitySignature } from "@/components/explorers/CapabilitySignature";
import { cn } from "@/lib/utils";

const FamilyBadge = ({ cap }) => (
  <span data-testid="capability-family-badge" className="inline-flex items-center gap-2 border border-o-strong px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em]">
    <span aria-hidden className="h-2 w-2" style={{ background: cap.hue }} />
    {cap.family === "ordo" ? "Ordo software family" : "Independent offering · installs on its own"}
  </span>
);

const ReachedFrom = ({ cap }) => (
  <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-o-line pt-5">
    <Mono>Also reached from</Mono>
    <TextLink to="/ordo#explorer" testid="reached-from-ordo">{cap.family === "ordo" ? "General Ordo architecture" : "Its relation to General Ordo"}</TextLink>
    <TextLink to={`/enterprise/software?capability=${cap.id}`} testid="reached-from-software">Applied software overview</TextLink>
  </div>
);

const Understanding = ({ cap }) => (
  <section className="border-b border-o-line" data-testid="capability-understanding">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <Eyebrow index="01">Responsibility</Eyebrow>
        <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">{cap.responsibility}</SectionTitle>
      </div>
      <div className="grid gap-10 sm:grid-cols-2 lg:col-span-8">
        <div><Mono accent>What it concerns</Mono><p className="mt-3 text-lg leading-relaxed">{cap.concerns}</p></div>
        <div><Mono accent>Why it matters</Mono><p className="mt-3 text-lg leading-relaxed">{cap.matters}</p></div>
        <div className="border border-dashed border-o-strong p-5 sm:col-span-2">
          <Mono>Reserved for Phase 3</Mono>
          <p className="mt-2 text-sm text-o-dim">Extended explanation of {cap.name}. This public page describes its responsibility, not its mechanism.</p>
        </div>
      </div>
    </Container>
  </section>
);

const Shape = ({ cap }) => (
  <section className="border-b border-o-line" data-testid="capability-shape">
    <Container className="py-16 lg:py-24">
      <Eyebrow index="02">Conceptual shape</Eyebrow>
      <div className="mt-8 border border-o-line bg-o-surface p-5 sm:p-8"><CapabilitySignature cap={cap} /></div>
    </Container>
  </section>
);

const Together = ({ cap }) => (
  <section className="border-b border-o-line" data-testid="capability-together">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <Eyebrow index="03">Alone or together</Eyebrow>
        <p className="mt-5 font-display text-2xl font-bold leading-snug">{cap.alone}</p>
        <p className="mt-4 text-sm text-o-dim">No organization is expected to adopt all four capabilities.</p>
      </div>
      <ul className="border-t border-o-line lg:col-span-8">
        {Object.entries(cap.pairs).map(([id, note]) => (
          <li key={id} className="grid gap-3 border-b border-o-line py-5 sm:grid-cols-12 sm:items-baseline">
            <div className="flex items-center gap-3 sm:col-span-4">
              <span aria-hidden className="h-2.5 w-2.5" style={{ background: CAPABILITIES[id].hue }} />
              <span className="font-display text-lg font-bold">{CAPABILITIES[id].name}</span>
            </div>
            <p className="text-o-dim sm:col-span-6">{note}</p>
            <div className="sm:col-span-2 sm:text-right">
              <TextLink to={CAPABILITIES[id].path} state={{ via: "software" }} testid={`pair-link-${id}`}>Open</TextLink>
            </div>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const Relation = ({ cap }) => (
  <section className="border-b border-o-line" data-testid="capability-relation">
    <Container className="grid gap-4 py-16 md:grid-cols-2 lg:py-20">
      <div className="border border-o-line bg-o-surface p-6 sm:p-8">
        <Mono accent>Relationship to General Ordo</Mono>
        <p className="mt-3 text-lg leading-relaxed">{cap.ordoRelation}</p>
        <TextLink to="/ordo#explorer" testid="capability-ordo-link" className="mt-5">Explore General Ordo</TextLink>
      </div>
      <div className="border border-dashed border-o-strong p-6 sm:p-8">
        <Mono>Not part of the applied inventory</Mono>
        <p className="mt-3 leading-relaxed text-o-dim">General Ordo Infrastructure is a separate licensing route for organizations building their own intelligent systems. It is not a fifth applied product.</p>
        <TextLink to="/ordo/infrastructure" testid="capability-infra-link" className="mt-5">General Ordo Infrastructure</TextLink>
      </div>
    </Container>
  </section>
);

const Switcher = ({ current }) => (
  <nav aria-label="Applied capabilities" data-testid="capability-switcher">
    <Container className="py-12">
      <Mono>All four applied capabilities</Mono>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {CAP_IDS.map((id) => {
          const c = CAPABILITIES[id];
          return (
            <Link key={id} to={c.path} state={{ via: "software" }} aria-current={id === current ? "page" : undefined} data-testid={`switcher-${id}`}
              className={cn("flex flex-col gap-3 border p-4 transition-colors", id === current ? "border-o-accent bg-o-soft" : "border-o-line hover:border-o-strong")}>
              <span aria-hidden className="h-1 w-10" style={{ background: c.hue }} />
              <span className="font-display text-lg font-bold leading-tight">{c.name}</span>
              <span className="text-sm text-o-dim">{c.responsibility}</span>
            </Link>
          );
        })}
      </div>
    </Container>
  </nav>
);

export default function CapabilityPage({ id }) {
  const cap = CAPABILITIES[id];
  const { state } = useLocation();
  const via = cap.family === "ordo" && state?.via === "software" ? "software" : undefined;
  const group = cap.family === "ordo" && !via ? "ordo" : "enterprise";
  return (
    <>
      <PageIntro routeKey={id} via={via} group={group} index={cap.index}
        eyebrow={`${cap.family === "ordo" ? "Ordo software family" : "Independent offering"} · ${cap.responsibility}`}
        title={cap.name} lede={cap.statement}
        aside={<Placeholder label={cap.placeholder.label} note={cap.placeholder.note} ratio="4 / 3" testid={`placeholder-${id}`} />}>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <FamilyBadge cap={cap} />
          <StatusMarker testid={`status-${id}`} />
        </div>
        <ReachedFrom cap={cap} />
      </PageIntro>
      <Understanding cap={cap} />
      <Shape cap={cap} />
      <Together cap={cap} />
      <Relation cap={cap} />
      <Switcher current={id} />
      <EngageBand path="business" secondary={cap.family === "ordo" ? "technical" : undefined} from={cap.path}
        title={`Discuss ${cap.name}.`} text="Through Ordo Diagnostic, or directly where appropriate. During Phase 1 this inquiry is an inactive preview." />
    </>
  );
}
